<template>
  <div :class="wrapperClasses">
    <template v-if="type === 'single'">
      <dp-datepicker-item
        :id="id"
        v-model="singleDate"
        :append-to-body="appendToBody"
        :class="calendarItemClasses"
        :disabled="disabled"
        :disabled-dates="disabledDates"
        :first-day-of-week="firstDayOfWeek"
        :fixed-position="fixedPosition"
        :format="format"
        :highlighted="highlighted"
        :inline="inline"
        :input-class="inputClass"
        :label="mergedTranslations.labelDate"
        :maximum-view="maximumView"
        :name="name"
        :open-date="openDate"
        :placeholder="placeholderPrimary"
        :required="required"
        :show-label="showLabel"
        :size="size"
        :translations="mergedTranslations"
        :typeable="typeable"
        :use-utc="useUtc"
        @blur="blur"
        @changed-decade="changedDecade"
        @changed-month="changedMonth"
        @changed-year="changedYear"
        @clear="cleared"
        @closed="closed"
        @focus="focus"
        @input="input"
        @opened="opened"
        @select="setSingleDate"
      >
        <!-- @slot Slot for content displayed below the calendar -->
        <slot/>

        <!-- @slot Slot for providing custom content inside day cell -->
        <template #dayCellContent="{ cell }">
          <slot
            :cell="cell"
            name="dayCellContent"
          />
        </template>

        <template #DpPrevIntervalBtn>
          <!-- @slot Override default prev button -->
          <slot name="prevIntervalBtn"/>
        </template>

        <template #DpNextIntervalBtn>
          <!-- @slot Override default next button -->
          <slot name="nextIntervalBtn"/>
        </template>
      </dp-datepicker-item>
    </template>

    <template v-else>
      <dp-datepicker-item
        :id="`${rangeFromPrefix}|${id}`"
        ref="datepickerRangeFrom"
        v-model="range.from"
        :append-to-body="appendToBody"
        :disabled="disabled"
        :disabled-dates="disabledDates"
        :first-day-of-week="firstDayOfWeek"
        :fixed-position="fixedPosition"
        :format="format"
        :highlighted="highlightedInRange"
        :inline="inline"
        :input-class="inputClass"
        :label="mergedTranslations.labelFrom"
        :maximum-view="maximumView"
        :name="`${rangeFromPrefix}|${name}`"
        :open-date="openDate"
        :placeholder="placeholderPrimary"
        :required="required"
        :show-label="showLabel"
        :size="size"
        :translations="mergedTranslations"
        :typeable="typeable"
        :use-utc="useUtc"
        @blur="blur"
        @changed-decade="changedDecade"
        @changed-month="changedMonth"
        @changed-year="changedYear"
        @clear="clearedFrom"
        @closed="closed"
        @focus="focus"
        @input="input"
        @opened="opened"
        @select="selectedRangeFrom"
      >
        <slot/>

        <template #dayCellContent="{ cell }">
          <slot
            :cell="cell"
            name="dayCellContent"
          />
        </template>

        <template #DpPrevIntervalBtn>
          <slot name="prevIntervalBtn"/>
        </template>

        <template #DpNextIntervalBtn>
          <slot name="nextIntervalBtn"/>
        </template>
      </dp-datepicker-item>

      <dp-datepicker-item
        :id="`${rangeToPrefix}|${id}`"
        ref="datepickerRangeTo"
        v-model="range.to"
        :append-to-body="appendToBody"
        class="ml-1"
        :disabled="disabled"
        :disabled-dates="disabledDates"
        :first-day-of-week="firstDayOfWeek"
        :fixed-position="fixedPosition"
        :format="format"
        :highlighted="highlightedInRange"
        :inline="inline"
        :input-class="inputClass"
        :label="mergedTranslations.labelTo"
        :maximum-view="maximumView"
        :name="`${rangeToPrefix}|${name}`"
        :placeholder="placeholderSecondary"
        :required="required"
        :show-label="showLabel"
        :size="size"
        :translations="mergedTranslations"
        :typeable="typeable"
        :use-utc="useUtc"
        @blur="blur"
        @changed-decade="changedDecade"
        @changed-month="changedMonth"
        @changed-year="changedYear"
        @clear="clearedTo"
        @closed="closed"
        @focus="focus"
        @input="input"
        @opened="opened"
        @select="selectedRangeTo"
      >
        <slot/>

        <template #dayCellContent="{ cell }">
          <slot
            :cell="cell"
            name="dayCellContent"
          />
        </template>

        <template #DpPrevIntervalBtn>
          <slot name="prevIntervalBtn"/>
        </template>

        <template #DpNextIntervalBtn>
          <slot name="nextIntervalBtn"/>
        </template>
      </dp-datepicker-item>
    </template>

    <!-- @slot Space for overriding custom validation feedback -->
    <slot name="validationFeedback">
      <div
        v-if="validationFeedbackText"
        :class="validationFeedbackClass"
      >
        {{ validationFeedbackText }}
      </div>
    </slot>
  </div>
</template>

<script>
import { SIZE_MD } from '../../config/constants';
import formControl from '../../mixins/form-control';
import { FORM_CONTROL_SIZES } from '../input/config';
import {
  DATEPICKER_BASE_CLASS,
  DATEPICKER_CALENDAR_ITEM_CLASS,
  DATEPICKER_DATE_FORMAT,
  DATEPICKER_DEFAULT_TRANSLATIONS,
  DATEPICKER_FIRST_DAY_OF_WEEK,
  DATEPICKER_MAXIMUM_VIEW,
  DATEPICKER_MAXIMUM_VIEW_MONTH,
  DATEPICKER_MODE_SINGLE,
  DATEPICKER_MODES,
  DATEPICKER_PLACEHOLDER,
  DATEPICKER_POSITIONS,
  DATEPICKER_RANGE_FROM_PREFIX,
  DATEPICKER_RANGE_TO_PREFIX,
} from './config';
import DpDatepickerItem from './DpDatepickerItem.vue';

export default {
  name: 'DpDatepicker',

  components: {
    DpDatepickerItem,
  },

  mixins: [formControl],

  props: {
    /**
     * @model modelValue
     */
    modelValue: {
      type: [Date, Object],
      required: false,
      default: null,
      validator: (date) => {
        const isRange = date && date.constructor.name === 'Object';
        const hasCorrectRangeFromDate = isRange && (date.from && typeof date.from.getMonth === 'function');
        const hasCorrectRangeToDate = isRange && (date.to && typeof date.to.getMonth === 'function');
        const hasCorrectSingleDate = date && typeof date.getMonth === 'function';

        if (!hasCorrectSingleDate && !isRange && !hasCorrectRangeFromDate && !hasCorrectRangeToDate) {
          return false;
        }

        if (!hasCorrectSingleDate && !isRange) {
          return false;
        }

        return true;
      },
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabledDates: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    firstDayOfWeek: {
      type: String,
      required: false,
      default: DATEPICKER_FIRST_DAY_OF_WEEK,
    },

    appendToBody: {
      type: Boolean,
      default: false,
    },

    /**
     * @values  auto, bottom, bottom-left, bottom-right, top, top-left, top-right
     * */
    fixedPosition: {
      type: String,
      default: 'auto',
      required: false,
      validator: (position) => DATEPICKER_POSITIONS.includes(position),
    },

    /**
     * @default "dd.MM.yyyy"
     */
    format: {
      type: String,
      required: false,
      default: DATEPICKER_DATE_FORMAT,
    },

    fullWidth: {
      type: Boolean,
      required: false,
      default: false,
    },

    highlighted: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    id: {
      type: String,
      required: false,
      default: '',
    },

    /**
     * Show datepicker always open
     */
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },

    inputClass: {
      type: [String, Object],
      required: false,
      default: '',
    },

    /**
     * @values day, month, year
     * @default month
     * */
    maximumView: {
      type: String,
      default: DATEPICKER_MAXIMUM_VIEW_MONTH,
      required: false,
      validator: (value) => DATEPICKER_MAXIMUM_VIEW.includes(value),
    },

    name: {
      type: String,
      required: false,
      default: '',
    },

    /**
     * If set, open on that date
     */
    openDate: {
      type: [String, Date],
      required: false,
      default: null,
    },

    /**
     * Provides placeholder text in datepicker or in the first input of date range
     * @default "dd.mm.yyyy"
     */
    placeholderPrimary: {
      type: String,
      required: false,
      default: DATEPICKER_PLACEHOLDER,
    },

    /**
     * Provides placeholder text in the second input of date range
     * @default "dd.mm.yyyy"
     */
    placeholderSecondary: {
      type: String,
      required: false,
      default: DATEPICKER_PLACEHOLDER,
    },

    required: {
      type: Boolean,
      required: false,
      default: false,
    },

    showLabel: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * @values sm, md, lg
     * @default md
     * */
    size: {
      type: String,
      default: SIZE_MD,
      required: false,
      validator: (value) => FORM_CONTROL_SIZES.includes(value),
    },

    /**
     * @values single, range
     * @default single
     */
    type: {
      type: String,
      required: false,
      default: DATEPICKER_MODE_SINGLE,
      validator: (mode) => DATEPICKER_MODES.includes(mode),
    },

    /*
     * Use UTC for time calculations
     */
    /**
     * See to config file for default values for translations
     */
    translations: {
      type: Object,
      required: false,
      default: () => DATEPICKER_DEFAULT_TRANSLATIONS,
    },

    /*
     * Sets html required attribute on input
     */
    /**
     * If true, allow the user to type the date
     */
    typeable: {
      type: Boolean,
      required: false,
      default: false,
    },

    useUtc: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: [
    'select',
    'update:modelValue',
    'blur',
    'clear',
    'clearFrom',
    'clearTo',
    'changedDecade',
    'changedMonth',
    'changedYear',
    'closed',
    'focus',
    'input',
    'opened',
  ],

  data: () => ({
    singleDate: null,
    range: {
      from: null,
      to: null,
      dates: [],
    },
  }),

  computed: {
    mergedTranslations() {
      return { ...DATEPICKER_DEFAULT_TRANSLATIONS, ...this.translations };
    },

    highlightedInRange() {
      return { ...this.range, ...this.highlighted };
    },

    rangeFromPrefix() {
      return DATEPICKER_RANGE_FROM_PREFIX;
    },

    rangeToPrefix() {
      return DATEPICKER_RANGE_TO_PREFIX;
    },

    wrapperClasses() {
      const { validationClass, fullWidth } = this;
      const classes = [DATEPICKER_BASE_CLASS];

      if (validationClass) {
        classes.push(validationClass);
      }

      if (fullWidth) {
        classes.push('d-flex');
      } else {
        classes.push('d-inline-flex');
      }

      return classes;
    },

    calendarItemClasses() {
      return DATEPICKER_CALENDAR_ITEM_CLASS;
    },
  },

  watch: {
    modelValue: {
      handler(value) {
        const isSingleDate = value && this.type === 'single';
        const isRange = value && this.type === 'range';
        const { range } = this;

        if (!isSingleDate && value === null) {
          this.setSingleDate(value, false);

          return;
        }

        if (isSingleDate) {
          this.setSingleDate(value, false);

          return;
        }

        if (!isRange && !value.from && !value.to) {
          return;
        }

        if (value.from === range.from && value.to === range.to) {
          return;
        }

        this.setRange({ ...value, emit: false });
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    setSingleDate(date, emit) {
      this.singleDate = date;

      if (emit === false) {
        return;
      }

      /**
       * Emits selected date
       *
       * @type {Date}
       */
      this.$emit('select', date);
      this.$emit('update:modelValue', date);
    },

    setRange(options) {
      const { range } = this;

      if (options.from) {
        range.from = options.from;
        range.dates.push(options.from);
      }

      if (options.to) {
        range.to = options.to;
        range.dates.push(options.to);
      }

      if (options.from === null) {
        range.from = options.from;
      }

      if (options.to === null) {
        range.to = options.to;
      }

      if (options.emit === false) {
        return;
      }

      /**
       * Emits selected date range
       *
       * @type {Object}
       */
      this.$emit('select', { ...range });
      this.$emit('update:modelValue', { ...range });
    },

    selectedRangeFrom(date) {
      this.setRange({ from: date });
      this.openDatepickerRangeTo();
    },

    selectedRangeTo(date) {
      this.setRange({ to: date });
    },

    cleared() {
      this.singleDate = null;
      /**
       * Emits clearing the date
       */
      this.$emit('update:modelValue', null);
      this.$emit('clear');
    },

    blur() {
      /**
       * Emits input blur event
       */
      this.$emit('blur');
    },

    changedDecade(date) {
      /**
       * Emits changed decade event
       * @type {Date}
       */
      this.$emit('changedDecade', date);
    },

    changedMonth(date) {
      /**
       * Emits changed month event
       * @type {Date}
       */
      this.$emit('changedMonth', date);
    },

    changedYear(date) {
      /**
       * Emits changed year event
       * @type {Date}
       */
      this.$emit('changedYear', date);
    },

    closed() {
      /**
       * Emits closed picker event
       */
      this.$emit('closed');
    },

    focus() {
      /**
       * Emits cinput focus event
       */
      this.$emit('focus');
    },

    input(date) {
      /**
       * Emits modified input value event
       * @type {Date}
       */
      this.$emit('input', date);
    },

    opened() {
      /**
       * Emits opened picker event
       */
      this.$emit('opened');
    },

    clearedFrom() {
      this.range.from = null;
      this.range.dates = [];
      /**
       * Emits clearing range from
       */
      this.$emit('clearFrom');
    },

    clearedTo() {
      this.range.to = null;
      /**
       * Emits clearing range to
       */
      this.$emit('clearTo');
    },

    openDatepickerRangeTo() {
      this.$refs.datepickerRangeTo.openDatepicker();
    },
  },
};
</script>

<style lang="scss">
@use "../../../scss/src/bootstrap/variables" as *;

@mixin datepicker-validation($color) {
  &.dp-datepicker {
    .form-control {
      border-color: $color;
    }

    .form-control ~ .input-group-append > .btn {
      border-color: $color;
    }
  }
}

.dp-datepicker {
  flex-wrap: wrap;
}

.is-valid {
  @include datepicker-validation($success);
}

.is-invalid {
  @include datepicker-validation($danger);
}
</style>
